// Thin API client — same-origin /api (nginx proxies to the backend in prod; the local
// tina4 dev server serves both). Bearer token attached from auth. JSON in/out.
import { token, refresh, logout } from './auth.js';
import { navigate } from './t4.js';
// Circular with impersonate.js (it POSTs its stop through `api`) — safe because BOTH sides only
// touch the other inside function bodies at call time, never during module evaluation.
import { impersonationHeader, recentlyImpersonating, clearImpersonation } from './impersonate.js';

const BASE = '/api';

// The SINGLE logout gate. A dead support-access grant must never take the admin's OWN session down
// with it: if we are (or just were, within recentlyImpersonating's window) impersonating, a 401
// means the grant died — drop impersonation and return to the admin console instead of signing the
// admin out. A genuine expired-session 401 (not impersonating) still logs out and bounces to login.
function forceLogin(){
  if(recentlyImpersonating()){ clearImpersonation(); navigate('/'); return; }
  logout();
  navigate('/login');
}

function authHeaders(){
  const headers = {};
  const t = token();
  if(t) headers['Authorization'] = `Bearer ${t}`;
  // Support-access impersonation rides on top of the admin's own bearer (X-Impersonate —
  // see shared/impersonate.js); null while inactive, and self-heals when the grant expires.
  Object.assign(headers, impersonationHeader() || {});
  return headers;
}

// Shared response handling for every verb: a 401 is silently refreshed once, then goes through the
// forceLogin gate (which returns an impersonating admin to their console rather than logging out).
// Short-circuit while impersonating so we don't burn a refresh + retry on the dead grant first.
async function finish(res, retry){
  if(res.status === 401){
    // Impersonation-end is NOT a session expiry: forceLogin already dropped the grant and sent the
    // admin to their console. Throw WITHOUT status:401 so a screen's own `if(e.status===401) logout`
    // catch (home.js, chart.js, …) doesn't undo that by logging the admin out to /login.
    if(recentlyImpersonating()){ forceLogin(); throw Object.assign(new Error('Support-access session ended'), { impersonationEnded: true }); }
    if(retry && await refresh()) return retry();
    forceLogin();
    throw Object.assign(new Error('Session expired — please sign in again'), { status: 401 });
  }
  const data = await res.json().catch(() => null);
  if(!res.ok){
    throw Object.assign(new Error((data && (data.error || data.message)) || `HTTP ${res.status}`),
                        { status: res.status, data });
  }
  return data;
}

async function req(method, path, body, _retried){
  const headers = authHeaders();
  const opts = { method, headers };
  if(body !== undefined){ headers['Content-Type'] = 'application/json'; opts.body = JSON.stringify(body); }
  const res = await fetch(BASE + path, opts);
  return finish(res, _retried ? null : () => req(method, path, body, true));
}

// Multipart upload (the CSV importer). NOTE: never set Content-Type here — the browser must set
// it itself so the multipart boundary is included; setting it by hand produces a body the server
// cannot parse. Everything else (bearer, 401-refresh, error shape) matches the JSON verbs.
async function upload(path, formData, _retried){
  const res = await fetch(BASE + path, { method: 'POST', headers: authHeaders(), body: formData });
  return finish(res, _retried ? null : () => upload(path, formData, true));
}

// Binary download (PDF / Excel exports). Bearer tokens rule out plain <a href> links, so:
// fetch with auth → Blob → a programmatic <a download> click. Filename comes from the
// server's Content-Disposition; `fallback` covers a proxy that strips it.
async function download(path, fallback, _retried){
  const res = await fetch(BASE + path, { headers: authHeaders() });
  if(res.status === 401){
    if(recentlyImpersonating()){ forceLogin(); throw Object.assign(new Error('Support-access session ended'), { impersonationEnded: true }); }
    if(!_retried && await refresh()) return download(path, fallback, true);
    forceLogin();
    throw Object.assign(new Error('Session expired — please sign in again'), { status: 401 });
  }
  if(!res.ok){
    const data = await res.json().catch(() => null);
    throw Object.assign(new Error((data && (data.error || data.message)) || `HTTP ${res.status}`),
                        { status: res.status, data });
  }
  const cd = res.headers.get('Content-Disposition') || '';
  const m = cd.match(/filename="?([^";]+)"?/i);
  const blob = await res.blob();
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = (m && m[1]) || fallback || 'download';
  a.click();
  URL.revokeObjectURL(a.href);
  return a.download;
}

// Authed blob → object URL, for <img>/<iframe> that can't carry a Bearer header themselves
// (statement page images + the source viewer). Caller MUST URL.revokeObjectURL when done.
async function objectUrl(path, _retried){
  const res = await fetch(BASE + path, { headers: authHeaders() });
  if(res.status === 401){
    if(recentlyImpersonating()){ forceLogin(); throw Object.assign(new Error('Support-access session ended'), { impersonationEnded: true }); }
    if(!_retried && await refresh()) return objectUrl(path, true);
    forceLogin();
    throw Object.assign(new Error('Session expired — please sign in again'), { status: 401 });
  }
  if(!res.ok){
    const data = await res.json().catch(() => null);
    throw Object.assign(new Error((data && (data.error || data.message)) || `HTTP ${res.status}`),
                        { status: res.status, data });
  }
  return URL.createObjectURL(await res.blob());
}

export const api = {
  get:      (p)    => req('GET', p),
  post:     (p, b) => req('POST', p, b),
  put:      (p, b) => req('PUT', p, b),
  patch:    (p, b) => req('PATCH', p, b),
  del:      (p)    => req('DELETE', p),
  upload:   (p, f) => upload(p, f),
  download: (p, fallback) => download(p, fallback),
  objectUrl: (p) => objectUrl(p),
};
